import { useThemeStore } from "@stores/theme.store";
import { useEffect, useState } from "react";
import { Button, Select, Tabs, TabsProps } from "antd";
import { useQuery } from "@tanstack/react-query";
import { SchoolService } from "@services/school.service";
import { HomeService } from "@services/home.service";
import { Spiner } from "@components/loaders/spiner";
import { toRp } from "@utils/helper";
import moment from "moment";
import {
  CaretDownFilled,
  CaretUpFilled,
  FilterOutlined,
} from "@ant-design/icons";
import { PopupCustom } from "@components/general/popup";

export const PocketInfo = () => {
  const { setHeader, setAppBar, setBottomNavigation } = useThemeStore(
    (state) => state,
  );

  const [popupFilter, setPopupFilter] = useState<boolean>(false);
  const [limit, setLimit] = useState("5");
  const [openDetails, setOpenDetails] = useState<number[]>([]);

  const { data: dataNis } = useQuery({
    queryKey: [SchoolService.queries.GET_SCHOOL_STUDENT_BIO],
    queryFn: SchoolService.studentBio,
  });
  const nis = dataNis?.data?.nis ?? "";

  const { data: dataSaldoUangSaku, isPending: isPendingUangSaku } = useQuery({
    queryKey: [HomeService.queries.GET_BILLS_AND_BALANCES, nis],
    queryFn: () => HomeService.billsAndBalances({ nis }),
  });

  const { data: laporanUangSaku, isPending: isPendingLaporanUangSaku } =
    useQuery({
      queryKey: [HomeService.queries.GET_DATA_REPORT_BALANCES, nis, limit],
      queryFn: () => HomeService.reportBalances({ nis, limit }),
    });

  const toggleDetail = (idx: number) => {
    setOpenDetails((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };

  useEffect(() => {
    setBottomNavigation("hidden");
    setAppBar({ title: "Uang Saku", show: true, leading: "back" });
    setHeader({
      buttonBack: true,
      filter: false,
      title: "Uang Saku",
      image: false,
    });
  }, [setHeader, setAppBar, setBottomNavigation]);

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "Masuk",
      children: (
        <>
          <div className="card-body">
            {(laporanUangSaku?.data ?? [])
              .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
              .filter((i) => i.tipe === "masuk")
              .slice(0, parseInt(limit))
              .map((item, idx) => (
                <div
                  className="tw-py-2 tw-border-[0] tw-border-b tw-border-gray-200 tw-border-solid last:tw-border-0"
                  key={idx}
                >
                  <div className="tw-flex tw-justify-between tw-items-center tw-font-semibold">
                    <div className="tw-text-xs tw-font-medium tw-text-accent-primary">
                      {moment(item.tanggal ?? Date.now()).format(
                        "DD MMMM YYYY",
                      )}
                    </div>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-start tw-font-semibold">
                    <div className="tw-text-sm">{item.catatan}</div>
                    <div className="tw-w-1/2 tw-text-end">
                      <div className="tw-text-base tw-text-text-primary">
                        {toRp(
                          parseFloat(
                            ((item.uangsakumasuk ?? "") + "").replaceAll(
                              ",",
                              "",
                            ) ?? 0,
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </>
      ),
    },
    {
      key: "2",
      label: "Keluar",
      children: (
        <>
          <div className="card-body">
            {(laporanUangSaku?.data ?? [])
              .sort((a, b) => moment(b.tanggal).diff(moment(a.tanggal)))
              .filter((i) => i.tipe === "keluar")
              .slice(0, parseInt(limit))
              .map((item, idx) => (
                <div
                  className="tw-py-2 tw-border-[0] tw-border-b tw-border-gray-200 tw-border-solid last:tw-border-0"
                  key={idx}
                >
                  <div className="tw-flex tw-justify-between tw-items-center tw-font-semibold">
                    <div className="tw-text-xs tw-font-medium tw-text-accent-primary">
                      {moment(item.tanggal ?? Date.now()).format(
                        "DD MMMM YYYY",
                      )}
                    </div>
                  </div>
                  <div className="tw-flex tw-justify-between tw-items-start tw-font-semibold">
                    <div className="tw-text-sm tw-w-1/2">{item.catatan}</div>
                    <div className="tw-w-1/2 tw-text-end">
                      <div className="tw-text-base tw-text-text-primary">
                        {toRp(
                          parseFloat(
                            ((item.uangkeluar ?? "") + "").replaceAll(
                              ",",
                              "",
                            ) ?? 0,
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                  {item.detail &&
                    item.detail.length > 0 &&
                    item.jenis !== "" && (
                      <>
                        <div
                          className="tw-mt-2 tw-cursor-pointer tw-flex tw-items-center tw-justify-between tw-gap-1 tw-text-xs tw-text-text-secondary"
                          onClick={() => toggleDetail(idx)}
                        >
                          <span>Detail ({item.detail.length})</span>
                          <span>
                            {openDetails.includes(idx) ? (
                              <CaretUpFilled />
                            ) : (
                              <CaretDownFilled />
                            )}
                          </span>
                        </div>
                        {openDetails.includes(idx) && (
                          <div className="tw-mt-2 tw-border-t tw-pt-2 tw-space-y-2">
                            {item.detail.map((d, dIdx) => (
                              <div
                                key={dIdx}
                                className="tw-flex tw-justify-between tw-text-xs tw-text-text-secondary"
                              >
                                {item.jenis.toLowerCase() === "ziswaf" ? (
                                  <>
                                    <span className="tw-w-1/2">
                                      {d.keterangan}
                                    </span>
                                    <span>
                                      {toRp(
                                        parseFloat(
                                          (d.nominal ?? "0").replace(",", ""),
                                        ),
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="tw-w-1/2">
                                      {d.tagihan}
                                    </span>
                                    <span>
                                      {toRp(
                                        parseFloat(
                                          (d.dibayar ?? "0").replace(",", ""),
                                        ),
                                      )}
                                    </span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                </div>
              ))}
          </div>
        </>
      ),
    },
  ];
  return (
    <>
      <div className="tw-px-4 tw-pt-2 tw-pb-20">
        {/* pocket highlight */}
        <div className="tw-pt-0 tw-pb-5 tw-text-background-100">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="pocket-highlight">
              <div className="tw-mb-2 tw-text-4xl tw-font-bold tw-text-background-100">
                {isPendingUangSaku ? (
                  <div className="tw-py-2">
                    <Spiner className="" />
                  </div>
                ) : (
                  toRp(dataSaldoUangSaku?.data?.saldo ?? 0)
                )}
              </div>
              <div className="tw-text-xs tw-text-background-100">
                total uang tersisa
              </div>
            </div>
            <div className="pocket-filter">
              <FilterOutlined
                className="tw-p-2 tw-text-xl tw-bg-transparent card-body"
                onClick={() => setPopupFilter(true)}
              />
            </div>
          </div>
        </div>
        {/* end of pocket highlight */}

        {/* finance report */}
        {/* <div className="tw-rounded-xl card-body">
          <div className="tw-text-xs tw-text-background-100 tw-mb-1">
            Filter Data
          </div>
          <Select
            suffixIcon={<FilterOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
            className="tw-text-base"
            placeholder="Pilih Jumlah Ditampilkan"
            defaultValue={limit}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setLimit(e)}
            options={[
              { value: "5", label: "5 data terakhir" },
              { value: "10", label: "10 data terakhir" },
              { value: "25", label: "25 data terakhir" },
            ]}
          ></Select>
        </div> */}

        <div className="tw-mt-6 two-tab">
          {isPendingLaporanUangSaku ? (
            <div className="tw-mt-6 tw-rounded-xl card-body">
              <div className="tw-py-2 tw-text-center">
                <Spiner className="" />
              </div>
            </div>
          ) : (
            <Tabs defaultActiveKey="1" items={items} />
          )}
        </div>
        {/* end of finance report */}
      </div>

      <PopupCustom
        title="Filter Data"
        open={popupFilter}
        onCancel={() => setPopupFilter(false)}
        slideUpModal={true}
      >
        <div className="">
          <Select
            className=" tw-text-base"
            placeholder="Pilih Jumlah Ditampilkan"
            defaultValue={limit}
            style={{ width: "100%" }}
            allowClear
            onChange={(e) => setLimit(e)}
            options={[
              { value: "5", label: "5 data terakhir" },
              { value: "10", label: "10 data terakhir" },
              { value: "25", label: "25 data terakhir" },
            ]}
          ></Select>
          <Button
            onClick={() => setPopupFilter(false)}
            className="tw-p-2 tw-mt-6 tw-h-10 tw-w-full tw-text-base tw-rounded-xl tw-bg-red-500 tw-text-background-200 focus:tw-shadow-none hover:tw-bg-background-200 hover:tw-text-accent-primary hover:tw-border-primary"
          >
            Tutup
          </Button>
        </div>
      </PopupCustom>
    </>
  );
};
